'use client';

import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { REGEXP_ONLY_DIGITS } from 'input-otp';

import { Label } from '@/src/shared/ui/label';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/src/shared/ui/input-otp';
import type { VerifyEmailFormValues } from '../model/types';

type Props = {
  control: Control<VerifyEmailFormValues>;
  errors: FieldErrors<VerifyEmailFormValues>;
};

const otpSlotClassName =
  'h-11 min-w-0 flex-1 rounded-lg border border-border/80 bg-background text-base shadow-xs first:rounded-lg last:rounded-lg first:border-l border-y border-r border-l';

export function VerifyEmailOtpField({ control, errors }: Props) {
  const t = useTranslations('auth.verifyEmail');

  function codeErrorMessage(code: string | undefined) {
    if (code === 'required') return t('errors.code.required');
    if (code === 'invalid_length' || code === 'invalid_format') return t('errors.code.invalid');
    return undefined;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="verify-code" className="text-xs font-medium text-muted-foreground">
        {t('fields.code.label')}
      </Label>
      <Controller
        name="code"
        control={control}
        render={({ field }) => (
          <InputOTP
            id="verify-code"
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS}
            value={field.value}
            onChange={field.onChange}
            aria-invalid={!!errors.code}
            aria-describedby={errors.code ? 'verify-code-error' : 'verify-code-hint'}
            containerClassName="w-full gap-3"
            className="w-full"
          >
            <InputOTPGroup className="flex flex-1 gap-2">
              <InputOTPSlot index={0} className={otpSlotClassName} />
              <InputOTPSlot index={1} className={otpSlotClassName} />
              <InputOTPSlot index={2} className={otpSlotClassName} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup className="flex flex-1 gap-2">
              <InputOTPSlot index={3} className={otpSlotClassName} />
              <InputOTPSlot index={4} className={otpSlotClassName} />
              <InputOTPSlot index={5} className={otpSlotClassName} />
            </InputOTPGroup>
          </InputOTP>
        )}
      />
      {!errors.code?.message && (
        <p id="verify-code-hint" className="text-xs text-muted-foreground">
          {t('fields.code.hint')}
        </p>
      )}
      {errors.code?.message && (
        <p id="verify-code-error" role="alert" className="text-xs text-destructive">
          {codeErrorMessage(errors.code.message)}
        </p>
      )}
    </div>
  );
}
